/**
 * Rhythmz — AI client v3
 * ───────────────────────
 * Model-aware browser-side wrapper for the edge AI gateway.
 * Uses the active settings from ModelSettings to pass provider,
 * endpoint, and model information to the gateway.
 *
 * Default: Cloudflare Workers AI (free, no keys needed).
 */

import { modelSettings } from './ModelSettings';

const BASE = '/ai';

export interface AIGenResult {
  preset: unknown;
  meta: { mood?: string; description?: string; ai: boolean; fallback?: boolean };
}

export interface GenerateResult {
  success: boolean;
  model: string;
  result?: string;
  error?: string;
  provider?: string;
}

export interface ComposeResult {
  success: boolean;
  model: string;
  blueprint: {
    root: string; scale: string; genre: string; bpm: number; bars: number;
  };
  chordProgression?: number[][];
}

export class AIClient {
  /** Build request body with active model settings plus overrides. */
  private buildBody(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const settings = modelSettings.getRequestPayload();
    return { ...settings, ...extra };
  }

  /** Generate a Milkdrop preset from a text prompt. */
  async generatePreset(prompt: string, opts: { tier?: 'free' | 'premium'; signal?: AbortSignal } = {}): Promise<AIGenResult> {
    const res = await fetch(`${BASE}/preset`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        prompt,
        tier: opts.tier ?? 'free',
        ...modelSettings.getRequestPayload(),
      }),
      signal: opts.signal,
    });
    if (!res.ok) throw new Error(`AI gateway error ${res.status}`);
    return await res.json() as AIGenResult;
  }

  /** Mood/energy analysis for an audio chunk. */
  async analyzeAudio(chunk: number[]): Promise<{ energy: number; mood: string; tilt: number } | null> {
    try {
      const res = await fetch(`${BASE}/analyze`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ chunk }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }

  /** Universal text generation with active model settings. */
  async generateText(
    prompt: string,
    opts: { system?: string; maxTokens?: number; temperature?: number } = {},
  ): Promise<GenerateResult> {
    try {
      const res = await fetch(`${BASE}/generate`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(this.buildBody({
          prompt,
          system: opts.system ?? 'You are a helpful assistant.',
          max_tokens: opts.maxTokens ?? 800,
          temperature: opts.temperature ?? 0.75,
        })),
      });
      return await res.json() as GenerateResult;
    } catch (e) {
      return { success: false, model: 'fetch-error', error: e instanceof Error ? e.message : String(e) };
    }
  }

  /** Music composition with active settings. */
  async composeMusic(
    genre: string,
    opts: { root?: string; scale?: string; bpm?: number; bars?: number; mood?: string } = {},
  ): Promise<ComposeResult> {
    try {
      const res = await fetch(`${BASE}/generate`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(this.buildBody({
          endpoint: 'compose',
          genre,
          root: opts.root ?? 'A',
          scale: opts.scale ?? 'minor',
          bpm: opts.bpm ?? 120,
          bars: opts.bars ?? 8,
          mood: opts.mood ?? '',
        })),
      });
      const data = await res.json() as ComposeResult & { error?: string };
      if (!data.success) throw new Error(data.error ?? 'Composition failed');
      return data;
    } catch {
      return {
        success: true, model: 'local-fallback',
        blueprint: {
          root: opts.root ?? 'A', scale: opts.scale ?? 'minor',
          genre, bpm: opts.bpm ?? 120, bars: opts.bars ?? 8,
        },
      };
    }
  }

  /** List all available models from the edge gateway. */
  async listModels(): Promise<{ available: number; models: { id: string; provider: string; tier: string; capabilities: string[]; speed: number; quality: number }[]; mobileRecommendation: string }> {
    try {
      const res = await fetch(`${BASE}/generate`, { method: 'GET' });
      if (!res.ok) throw new Error('gateway unavailable');
      return await res.json();
    } catch {
      return { available: 0, models: [], mobileRecommendation: 'offline' };
    }
  }

  /** Generate a real audio file from a text prompt via cloud AI (MiniMax Music 2.6 or Lyria 3). */
  async generateMusic(
    prompt: string,
    opts: { instrumental?: boolean; lyrics?: string; provider?: 'minimax' | 'lyria' } = {},
  ): Promise<{ success: boolean; audio?: string; provider?: string; error?: string }> {
    try {
      const res = await fetch(`${BASE}/music`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          prompt,
          instrumental: opts.instrumental ?? true,
          lyrics: opts.lyrics,
          provider: opts.provider ?? 'minimax',
        }),
      });
      if (!res.ok) throw new Error(`music gateway error ${res.status}`);
      return await res.json();
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : String(e) };
    }
  }
}

export const aiClient = new AIClient();